import {Inject, Injectable, Optional, PLATFORM_ID} from '@angular/core';

import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {isPlatformBrowser} from '@angular/common';

export class LoggerConfig {
    level: LoggerLevel;
    serverLogLevel: LoggerLevel;
    serverLoggingUrl?: string;
}

export enum LoggerLevel {
    TRACE = 0, DEBUG, INFO, LOG, WARN, ERROR, OFF
}

export interface LoggingHandler {
    log(level: LoggerLevel, message: string, ...args: Array<any>): void;
}

const Levels = [
    'TRACE',
    'DEBUG',
    'INFO',
    'LOG',
    'WARN',
    'ERROR',
    'OFF'
];

@Injectable()
export class Logger {
    private _serverLogLevel: LoggerLevel;
    private _clientLogLevel: LoggerLevel;
    private _isIE: boolean;
    private handlers: Array<LoggingHandler> = [];

    constructor(private readonly http: HttpClient,
                @Inject(PLATFORM_ID) private readonly platformId,
                @Optional() private readonly options?: LoggerConfig) {

        this.options = this.options ? this.options : {
            level: LoggerLevel.OFF,
            serverLogLevel: LoggerLevel.OFF
        };
        this._serverLogLevel = this.options.serverLogLevel;
        this._clientLogLevel = this.options.level;
        this._isIE = isPlatformBrowser(platformId) &&
            !!(navigator.userAgent.indexOf('MSIE') !== -1 || navigator.userAgent.match(/Trident\//) || navigator.userAgent.match(/Edge\//));
    }

    public registerHandler(handler: LoggingHandler) {
        this.handlers.push(handler);
    }

    public trace(message, ...additional: any[]): void {
        this._log(LoggerLevel.TRACE, true, message, additional);
    }

    public debug(message, ...additional: any[]): void {
        this._log(LoggerLevel.DEBUG, true, message, additional);
    }

    public info(message, ...additional: any[]): void {
        this._log(LoggerLevel.INFO, true, message, additional);
    }

    public log(message, ...additional: any[]): void {
        this._log(LoggerLevel.LOG, true, message, additional);
    }

    public warn(message, ...additional: any[]): void {
        this._log(LoggerLevel.WARN, true, message, additional);
    }

    public error(message, ...additional: any[]): void {
        this._log(LoggerLevel.ERROR, true, message, additional);
    }

    private _timestamp(): string {
        return new Date().toISOString();
    }

    private _logOnServer(level: LoggerLevel, message, additional: any[] = []): void {
        // If the loggingUrl is not set or if the user provides a serverLogLevel and the current level is than that, do not log.
        if (!this.options.serverLoggingUrl || level < this._serverLogLevel) {
            return;
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json');

        let messageToLog = '';

        try {
            messageToLog = JSON.stringify(message);
        } catch (e) {
            messageToLog = 'The provided "message" value could not be parsed with JSON.stringify().';
        }

        let additionalToLog: Array<string | null | undefined> = [];

        if (additional === null || additional === undefined) {
            additionalToLog = [];
        } else {
            additionalToLog = additional.map((val: any, idx: number) => {
                try {
                    return val === null || val === undefined || typeof val === 'string' ? val
                        : JSON.stringify(val, null, 2);
                } catch (e) {
                    return `The additional[${idx}] value could not be parsed using JSON.stringify().`;
                }
            });
        }

        this.http.post(this.options.serverLoggingUrl,
            {
                level: Levels[level],
                message: messageToLog,
                additional: additionalToLog,
                timestamp: this._timestamp()
            },
            {
                headers
            })
            .subscribe(
                (res: any) => this._log(LoggerLevel.TRACE, false, 'Server logging successful', [res]),
                (error: HttpErrorResponse) => this._log(LoggerLevel.ERROR, false, 'FAILED TO LOG ON SERVER', [error])
            );
    }

    private _logIE(level: LoggerLevel, message: string, additional: any[] = []): void {
        switch (level) {
            case LoggerLevel.WARN:
                console.warn(`${this._timestamp()} [${Levels[level]}] `, message, ...additional);
                break;
            case LoggerLevel.ERROR:
                console.error(`${this._timestamp()} [${Levels[level]}] `, message, ...additional);
                break;
            case LoggerLevel.INFO:
                console.info(`${this._timestamp()} [${Levels[level]}] `, message, ...additional);
                break;
            default:
                console.log(`${this._timestamp()} [${Levels[level]}] `, message, ...additional);
        }
    }

    private _log(level: LoggerLevel, logOnServer: boolean, message, additional: any[] = []): void {
        if (!message) {
            return;
        }

        try {
            if (message instanceof Error) {
                message = message.stack;
            } else if (typeof message !== 'string') {
                message = JSON.stringify(message, null, 2);
            }
        } catch (e) {
            additional = [message, ...additional];
            message = 'The provided "message" value could not be parsed with JSON.stringify().';
        }

        // Allow logging on server even if client log level is off
        if (logOnServer) {
            this._logOnServer(level, message, additional);
        }

        // if no message or the log level is less than the environ
        if (level < this._clientLogLevel) {
            return;
        }

        // Coloring doesn't work in IE
        if (this._isIE) {
            return this._logIE(level, message, additional);
        }

        const color = this._getColor(level);

        this.handlers.forEach((h) => {
            h.log(level, `${this._timestamp()} [${Levels[level]}] ${message}`, ...additional);
        });
        console.log(`%c${this._timestamp()} [${Levels[level]}]`, `color:${color}`, message, ...additional);
    }

    private _getColor(level: LoggerLevel): 'blue' | 'teal' | 'gray' | 'red' | undefined {
        switch (level) {
            case LoggerLevel.TRACE:
                return 'blue';
            case LoggerLevel.DEBUG:
                return 'teal';
            case LoggerLevel.INFO:
            case LoggerLevel.LOG:
                return 'gray';
            case LoggerLevel.WARN:
            case LoggerLevel.ERROR:
                return 'red';
            case LoggerLevel.OFF:
            default:
                return;
        }
    }

}


// WEBPACK FOOTER //
// ./src/app/common/services/logger.service.ts